import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDaysOff, useDaysOffMutations } from "@/api/sprints";
import type { DayOff } from "@/api/types/DayOff";
import { Dialog } from "@/components/Dialog";
import { useToast } from "@/components/Toast";
import { Button, Input, Spinner } from "@/components/ui";
import { daysBetween } from "@/lib/sprints";

/** Someone's days off: add, edit and remove ranges. `userId = null`: mine. */
export function DaysOffEditor({ userId }: { userId: string | null }) {
  const { t } = useTranslation();
  const toast = useToast();
  const list = useDaysOff(userId);
  const m = useDaysOffMutations(userId);
  const [editing, setEditing] = useState<DayOff | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const bad = !from || !to || to < from;
  const reset = () => {
    setEditing(null);
    setFrom("");
    setTo("");
    setNote("");
  };
  const err = { onError: (e: unknown) => toast.error(e) };
  const submit = () => {
    if (bad) return;
    const body = { starts_on: from, ends_on: to, note };
    if (editing) m.update.mutate({ id: editing.id, ...body }, { ...err, onSuccess: reset });
    else m.add.mutate(body, { ...err, onSuccess: reset });
  };
  const fmt = (d: string) => new Date(`${d}T00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-sm text-muted">{t("sprints.daysOff.hint")}</p>
      {list.isPending ? (
        <Spinner />
      ) : list.data?.length ? (
        <ul className="m-0 flex list-none flex-col divide-y divide-border rounded-[var(--radius-card)] border border-border p-0">
          {list.data.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
              <span className="font-medium tabular-nums">
                {fmt(d.starts_on)}
                {d.ends_on !== d.starts_on && ` – ${fmt(d.ends_on)}`}
              </span>
              <span className="text-xs text-muted">{t("sprints.daysOff.nDays", { count: daysBetween(d.starts_on, d.ends_on) + 1 })}</span>
              {d.note && <span className="min-w-0 truncate text-text-2">{d.note}</span>}
              <span className="ml-auto flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditing(d);
                    setFrom(d.starts_on);
                    setTo(d.ends_on);
                    setNote(d.note);
                  }}
                >
                  {t("sprints.daysOff.edit")}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => m.remove.mutate(d.id, err)} aria-label={`${t("sprints.daysOff.remove")} ${d.starts_on}`}>
                  {t("sprints.daysOff.remove")}
                </Button>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 text-sm text-muted">{t("sprints.daysOff.none")}</p>
      )}
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <label className="flex flex-col gap-1 text-xs text-muted">
          {t("sprints.daysOff.from")}
          <Input
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              if (!to || to < e.target.value) setTo(e.target.value);
            }}
            className="h-8 w-40"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted">
          {t("sprints.daysOff.to")}
          <Input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="h-8 w-40" />
        </label>
        <label className="flex min-w-40 flex-1 flex-col gap-1 text-xs text-muted">
          {t("sprints.daysOff.note")}
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("sprints.daysOff.notePlaceholder")} maxLength={200} className="h-8" />
        </label>
        <Button size="sm" variant="primary" type="submit" disabled={bad} loading={m.add.isPending || m.update.isPending}>
          {editing ? t("sprints.daysOff.save") : t("sprints.daysOff.add")}
        </Button>
        {editing && (
          <Button size="sm" type="button" onClick={reset}>
            {t("common.cancel")}
          </Button>
        )}
      </form>
      <p className="m-0 text-xs text-muted">{t("sprints.daysOff.merged")}</p>
    </div>
  );
}

/** A button opening someone's days off in a dialog (managers, on the members page). */
export function DaysOffButton({ userId, name }: { userId: string; name: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="ghost" icon="calendar" onClick={() => setOpen(true)}>
        {t("sprints.daysOff.button")}
      </Button>
      {open && (
        <Dialog open onClose={() => setOpen(false)} title={t("sprints.daysOff.titleFor", { name })} width={560}>
          <DaysOffEditor userId={userId} />
        </Dialog>
      )}
    </>
  );
}
